import { Button, Tag, Tooltip, theme } from 'antd';
import { activeLocale } from '@/utils/locale';
import { useLanguage } from '@/hooks/useLanguage';
import { FolderOpenOutlined, DeleteOutlined, PlusOutlined, CopyOutlined, EditOutlined } from '@ant-design/icons';
import { ProviderIcon } from '@/components/ProviderIcon';
import { formatCurrency } from '../services/costEngine';
import type { PricingPayload, SavedScenario } from '../types';

interface Props {
  scenario: SavedScenario;
  pricing: PricingPayload;
  /** Show "Load" button (Simulator tab) */
  onLoad?: () => void;
  /** Show "Use in Plan" button (Roadmap tab) */
  onUseInPlan?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  /** Show "Edit name & description" pencil button (Library modal). */
  onEdit?: () => void;
  /** Highlight this card as the currently-loaded one (Simulator tab edit-mode). */
  isLoaded?: boolean;
}

/**
 * Rich scenario card — shows agents/models/footprint at a glance so users
 * can pick the right scenario without loading every one. Used on both the
 * Simulator (Saved Scenarios section) and Roadmap (scenario picker) tabs.
 */
export function ScenarioCard({ scenario, pricing, onLoad, onUseInPlan, onDelete, onDuplicate, onEdit, isLoaded }: Props) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const params: any = scenario.parameters;
  const result: any = scenario.result;
  const agents: any[] = params?.agents ?? [];

  // The Library renders TWO scenario shapes:
  //   - Advanced (legacy): numSubTenants, agents[].activeUsersPerMonth, agents[].model, agents[].name, result.grandTotal
  //   - Simple-sim (new):  _kind:'simple', num_tenants, active_users, agents[].cost_per_conv_usd, agents[].convs_per_user_per_month, agents[].label, result.monthly_recurring
  // Both must render meaningfully so the user can pick the right one in
  // the Roadmap scenario picker.
  const isSimple = params?._kind === 'simple';

  const numSubTenants = isSimple
    ? (params?.num_tenants ?? 1)
    : (params?.numSubTenants ?? 1);

  const totalActiveUsers = isSimple
    ? (params?.active_users ?? 0) * numSubTenants
    : numSubTenants * agents.reduce((s, a) => s + (a.activeUsersPerMonth ?? 0), 0);

  const totalFiles = agents.reduce(
    (s, a) => s + (isSimple ? (a.files_count ?? 0) : (a.filesCount ?? 0)),
    0,
  );

  // Distinct providers — simple-sim doesn't store a model id per agent (cost-per-conv
  // already bakes the rate in), so provider chips just show what was implied by
  // the measured/manual entry. We surface nothing rather than wrong data.
  const providers = isSimple
    ? []
    : Array.from(new Set(
        agents.map(a => pricing.models.find(m => m.id === a.model)?.provider).filter(Boolean) as string[],
      ));

  // Headline cost — different field names between shapes.
  const monthlyCost = isSimple
    ? result?.monthly_recurring
    : result?.grandTotal;

  const savedDate = scenario.updated_at
    ? new Date(scenario.updated_at).toLocaleDateString(activeLocale(), { month: 'short', day: 'numeric' })
    : '';

  return (
    <div style={{
      padding: 12,
      background: isLoaded ? token.colorPrimaryBg : token.colorBgContainer,
      border: `1px solid ${isLoaded ? token.colorPrimary : token.colorBorderSecondary}`,
      borderRadius: token.borderRadiusLG,
      display: 'flex', flexDirection: 'column', gap: 10,
      transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
    }}>
      {/* Header row: name + saved date + total cost */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: token.colorText, lineHeight: 1.2 }} dir="auto">
            {scenario.name}
          </div>
          {savedDate && (
            <div style={{ fontSize: 10, opacity: 0.5 }}>Saved {savedDate}</div>
          )}
        </div>
        {monthlyCost != null && (
          <Tooltip title={t('costSimulator.monthlyRecurringAtSave')}>
            <div style={{ fontSize: 14, fontWeight: 700, color: token.colorPrimary, fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(monthlyCost)}<span style={{ fontSize: 10, fontWeight: 400, opacity: 0.6 }}>/mo</span>
            </div>
          </Tooltip>
        )}
      </div>

      {/* Optional description — shown only when set */}
      {scenario.description && (
        <div style={{
          fontSize: 12, lineHeight: 1.45, color: token.colorTextSecondary,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {scenario.description}
        </div>
      )}

      {/* Provider icons */}
      {providers.length > 0 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {providers.map(p => (
            <span key={p} title={p} style={{ color: token.colorTextSecondary }}>
              <ProviderIcon provider={p} size={14} />
            </span>
          ))}
        </div>
      )}

      {/* Footprint chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {isSimple && (
          <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>simple-sim</Tag>
        )}
        <Tag style={{ fontSize: 10, margin: 0 }}>{agents.length} {agents.length === 1 ? 'agent' : 'agents'}</Tag>
        <Tag style={{ fontSize: 10, margin: 0 }}>{numSubTenants} {numSubTenants === 1 ? 'tenant' : 'tenants'}</Tag>
        <Tag style={{ fontSize: 10, margin: 0 }}>{t('costSimulator.nUsers', { count: totalActiveUsers.toLocaleString() })}</Tag>
        {totalFiles > 0 && <Tag style={{ fontSize: 10, margin: 0 }}>{t('costSimulator.nFiles', { count: totalFiles })}</Tag>}
        {(isSimple ? (params?.insight_templates_count > 0) : params?.insightsEnabled) && (
          <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>insights</Tag>
        )}
      </div>

      {/* One-line agent summary */}
      <div style={{ fontSize: 11, opacity: 0.7, lineHeight: 1.4, wordBreak: 'break-word' }}>
        {agents.slice(0, 3).map(a => {
          if (isSimple) {
            // Simple-sim doesn't carry a per-agent model id (the cost-per-conv
            // already encodes the chosen model). Show the label + $/conv.
            const label = a.label ?? a.id ?? 'agent';
            const cpc = typeof a.cost_per_conv_usd === 'number'
              ? `$${a.cost_per_conv_usd.toFixed(6)}/conv`
              : '';
            return `${label} ${cpc}`.trim();
          }
          const m = pricing.models.find(mm => mm.id === a.model);
          return `${a.name} (${m?.name ?? a.model})`;
        }).join(' · ')}
        {agents.length > 3 && ` +${agents.length - 3} more`}
      </div>

      {/* Action row */}
      <div style={{ display: 'flex', gap: 6, marginBlockStart: 2 }}>
        {onLoad && (
          <Button size="small" icon={<FolderOpenOutlined />} onClick={onLoad}>
            {t('common.load')}
          </Button>
        )}
        {onUseInPlan && (
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={onUseInPlan}>
            {t('costSimulator.useInPlan')}
          </Button>
        )}
        {onDuplicate && (
          <Tooltip title={t('common.duplicate')}>
            <Button size="small" icon={<CopyOutlined />} onClick={onDuplicate} />
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip title={t('common.editNameDescription')}>
            <Button size="small" icon={<EditOutlined />} onClick={onEdit} />
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title={t('common.delete')}>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={onDelete} />
          </Tooltip>
        )}
      </div>
    </div>
  );
}
